import { useQueryClient } from '@tanstack/react-query';
import { LocationSearchResult } from '../../shared/location-search-result';
import { Point } from '../../types';
import { searchPlaceByText } from '../queries/searchPlaceByText';
import { MapData } from '../../shared/map-data';
import { Viewport } from '../../shared/viewport';
import React from 'react';

export const normalizeTextForSearch = (text: string): string => {
  const pattern = /_|-|'|\//g; // remove common word separation characters
  return text.toLowerCase().replace(pattern, '');
};

export function useSearchLocation() {
  const [datasourceNamesForSearch, setDatasourceNamesForSearch] = React.useState<string[] | null>(null);
  const queryClient = useQueryClient();

  const searchLocationsByQuery = React.useCallback(
    async (
      query: string,
      limit: number,
      referencePoint?: Point,
      boundingBox?: number[][]
    ): Promise<LocationSearchResult[]> => {
      const response = await searchPlaceByText(queryClient, query, limit, referencePoint, boundingBox);
      return response.data.places.map(({ location, displayName, address }) => {
        const searchDisplayName = `${displayName} (${address})`;
        return LocationSearchResult.fromPlacesResult(searchDisplayName, location, referencePoint, boundingBox);
      });
    },
    [queryClient]
  );

  const datasourceSearch = React.useCallback(
    (query: string, mapData: MapData[], referencePoint?: Point): LocationSearchResult[] => {
      const queryForMatching = normalizeTextForSearch(query);
      return (
        datasourceNamesForSearch
          ?.map<[string, number]>((name, index) => [name, index])
          .filter(([name]) => name.includes(queryForMatching))
          .map(([, index]) => LocationSearchResult.fromMapData(mapData[index], referencePoint)) ?? []
      );
    },
    [datasourceNamesForSearch]
  );

  const searchLocation = React.useCallback(
    async (
      query: string,
      viewport: Viewport | null,
      mapData: MapData[],
      boundingBox?: number[][],
      limit: number = 5
    ): Promise<LocationSearchResult[]> => {
      const referencePoint: Point | undefined = viewport
        ? { type: 'Point', coordinates: [viewport.longitude, viewport.latitude] }
        : undefined;
      try {
        const datasourceResults = datasourceSearch(query, mapData, referencePoint);
        const placesResults = await searchLocationsByQuery(query, limit, referencePoint, boundingBox);
        const allResults: LocationSearchResult[] = [...datasourceResults, ...placesResults];

        // Sort results in descending order of score
        allResults.sort((a, b) => b.rankingScore - a.rankingScore);
        return allResults.slice(0, limit);
      } catch (error) {
        console.error('Error searching location:', error);
      }
      return [];
    },
    [datasourceNamesForSearch, queryClient]
  );

  const updateSearchCache = React.useCallback((mapData: MapData[]): void => {
    setDatasourceNamesForSearch(mapData.map((d) => normalizeTextForSearch(d.datasourceName)));
  }, []);

  return {
    datasourceNamesForSearch,
    setDatasourceNamesForSearch,
    searchLocation,
    updateSearchCache,
  };
}
